/**
 * ORDENAR QUALQUER TABELA PELO TÍTULO DA COLUNA (dono, 28/09/2026) — o site inteiro, sem cada
 * tela repetir código: clicar no título ordena; clicar de novo inverte. Mesmo padrão do
 * `rotularTabelasDoApp`: um observador só, funções puras testadas.
 *
 *   - Entende R$, %, números com milhar (1.234,56), sinal (−R$ 49,90), datas (27/09/2026, 27/09,
 *     com hora), horas (14:30) e texto (ordem do português, "Item 2" antes de "Item 10").
 *   - Número e data começam do MAIOR; texto começa do A. "—" e vazio vão sempre para o fim.
 *   - Linha de total (`data-fixa` ou classe com "total") fica embaixo, na ordem de antes.
 *   - `data-ordem` numa célula dá o valor exato para ordenar (quando o texto não basta).
 *   - Fica de fora a tabela (ou área) com `data-sem-ordenar`: folhas de impressão, listas
 *     agrupadas e tabelas já ordenadas/paginadas pelo servidor. E, sozinha, a tabela cujo
 *     cabeçalho junta colunas ou cujas linhas não batem com o cabeçalho (ordenar quebraria).
 *
 * O React continua dono das linhas: quando a tela troca/adiciona linhas, a ordem escolhida é
 * reaplicada — e só mexe no DOM se estiver fora de ordem (senão entraria em laço com o observador).
 */

export type ValorDaCelula =
  | { readonly tipo: 'vazio' }
  | { readonly tipo: 'numero'; readonly n: number }
  | { readonly tipo: 'texto'; readonly s: string };

const VAZIOS = new Set(['', '—', '–', '-', '…', 'n/d', 'sem']);

/** O valor que ordena, a partir do texto da célula. */
export function valorDaCelula(bruto: string): ValorDaCelula {
  const t = bruto.replace(/\s+/g, ' ').trim();
  if (VAZIOS.has(t.toLowerCase())) return { tipo: 'vazio' };
  // data: 27/09/2026 [14:30] · 27/09 [14:30] (com ou sem o dia da semana depois)
  const d = /^(\d{2})\/(\d{2})(?:\/(\d{4}))?(?:,?\s+(?:às\s+)?(\d{2}):(\d{2}))?/.exec(t);
  if (d) {
    const [, dia, mes, ano, h, m] = d;
    return {
      tipo: 'numero',
      n:
        Number(ano ?? 0) * 1e8 +
        Number(mes) * 1e6 +
        Number(dia) * 1e4 +
        Number(h ?? 0) * 100 +
        Number(m ?? 0),
    };
  }
  const hora = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
  if (hora)
    return {
      tipo: 'numero',
      n: Number(hora[1]) * 3600 + Number(hora[2]) * 60 + Number(hora[3] ?? 0),
    };
  // número: [−] [R$] [−] 1.234,56 [% | un. | p.p. | ×] — o que vem depois do número não importa
  const num = /^([+\-−]?)\s*(?:R\$\s*)?([+\-−]?)\s*(\d{1,3}(?:\.\d{3})+|\d+)(,\d+)?(?![\d/:])/.exec(
    t,
  );
  if (num) {
    const negativo = num[1] === '-' || num[1] === '−' || num[2] === '-' || num[2] === '−';
    const inteiro = (num[3] ?? '0').replace(/\./g, '');
    const n = Number(`${inteiro}.${(num[4] ?? ',0').slice(1)}`);
    return { tipo: 'numero', n: negativo ? -n : n };
  }
  return { tipo: 'texto', s: t };
}

const colar = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/** A coluna é numérica se a maioria (80%) das células com valor for número/data. */
export function colunaEhNumerica(valores: readonly ValorDaCelula[]): boolean {
  const cheios = valores.filter((v) => v.tipo !== 'vazio');
  if (!cheios.length) return false;
  return cheios.filter((v) => v.tipo === 'numero').length / cheios.length >= 0.8;
}

/**
 * A nova ordem das linhas (índices), estável: empate mantém a ordem de antes. Vazios no fim,
 * nos dois sentidos.
 */
export function ordemDasLinhas(
  valores: readonly ValorDaCelula[],
  sentido: 'asc' | 'desc',
  numerica: boolean,
): number[] {
  const s = sentido === 'asc' ? 1 : -1;
  return valores
    .map((v, i) => ({ v, i }))
    .sort((a, b) => {
      const va = a.v;
      const vb = b.v;
      if (va.tipo === 'vazio' || vb.tipo === 'vazio')
        return va.tipo === vb.tipo ? a.i - b.i : va.tipo === 'vazio' ? 1 : -1;
      // coluna de números com um texto solto: o texto vai depois dos números, nos dois sentidos
      if (numerica && va.tipo !== vb.tipo) return va.tipo === 'numero' ? -1 : 1;
      let c: number;
      if (numerica && va.tipo === 'numero' && vb.tipo === 'numero') c = va.n - vb.n;
      else
        c = colar.compare(
          va.tipo === 'texto' ? va.s : String(va.n),
          vb.tipo === 'texto' ? vb.s : String(vb.n),
        );
      return c * s || a.i - b.i;
    })
    .map((x) => x.i);
}

/* ------------------------------------------------------------------ DOM */

interface Estado {
  readonly coluna: number;
  readonly sentido: 'asc' | 'desc';
}
const estados = new WeakMap<HTMLTableElement, Estado>();

const textoDe = (el: Element) => el.getAttribute('data-ordem') ?? el.textContent ?? '';
const ehFixa = (tr: HTMLTableRowElement) =>
  tr.hasAttribute('data-fixa') || /(^|[\s-])total([\s-]|$)/.test(tr.className);

/** A linha do cabeçalho que tem os títulos, se a tabela pode ser ordenada. */
function cabecalhoDe(tabela: HTMLTableElement): HTMLTableRowElement | null {
  if (tabela.closest('[data-sem-ordenar]')) return null;
  const cab = tabela.tHead;
  const linha = cab?.rows[cab.rows.length - 1];
  if (!linha || cab.rows.length > 1) return null;
  if (Array.from(linha.cells).some((c) => c.colSpan > 1)) return null;
  return linha;
}

/** A coluna aceita ordenar: tem título e não tem controle próprio (botão, caixa de marcar…). */
const colunaOrdenavel = (th: HTMLTableCellElement) =>
  (th.textContent ?? '').trim() !== '' && !th.querySelector('button, input, select, a');

function linhasDe(tabela: HTMLTableElement, colunas: number) {
  const corpo = tabela.tBodies[0];
  if (!corpo) return null;
  const todas = Array.from(corpo.rows);
  const moveis = todas.filter((tr) => !ehFixa(tr));
  // linha que junta colunas (grupo, aviso) ou com número de células diferente: não dá para ordenar
  if (
    moveis.some(
      (tr) => tr.cells.length !== colunas || Array.from(tr.cells).some((c) => c.colSpan > 1),
    )
  )
    return null;
  return { corpo, moveis, fixas: todas.filter(ehFixa) };
}

/** Aplica a ordem guardada; não toca no DOM se já está nela. */
function aplicar(tabela: HTMLTableElement): void {
  const e = estados.get(tabela);
  const cab = cabecalhoDe(tabela);
  if (!e || !cab) return;
  const ls = linhasDe(tabela, cab.cells.length);
  if (!ls) return;
  const valores = ls.moveis.map((tr) => valorDaCelula(textoDe(tr.cells[e.coluna] as Element)));
  const ordem = ordemDasLinhas(valores, e.sentido, colunaEhNumerica(valores));
  const desejada = [...ordem.map((i) => ls.moveis[i] as HTMLTableRowElement), ...ls.fixas];
  const atual = Array.from(ls.corpo.rows);
  if (desejada.every((tr, i) => atual[i] === tr)) return;
  for (const tr of desejada) ls.corpo.appendChild(tr);
}

function marcarCabecalho(tabela: HTMLTableElement): void {
  const cab = cabecalhoDe(tabela);
  if (!cab) return;
  const e = estados.get(tabela);
  Array.from(cab.cells).forEach((th, i) => {
    if (!colunaOrdenavel(th)) return;
    if (!th.hasAttribute('data-ordenavel')) {
      th.setAttribute('data-ordenavel', '');
      th.tabIndex = 0;
    }
    const aria = e?.coluna === i ? (e.sentido === 'asc' ? 'ascending' : 'descending') : null;
    if (aria) th.setAttribute('aria-sort', aria);
    else th.removeAttribute('aria-sort');
  });
}

function aoEscolher(th: HTMLTableCellElement): void {
  const tabela = th.closest('table');
  if (!(tabela instanceof HTMLTableElement)) return;
  const cab = cabecalhoDe(tabela);
  if (!cab || th.parentElement !== cab || !colunaOrdenavel(th)) return;
  const coluna = Array.from(cab.cells).indexOf(th);
  const ls = linhasDe(tabela, cab.cells.length);
  if (!ls || ls.moveis.length < 2) return;
  const antes = estados.get(tabela);
  let sentido: 'asc' | 'desc';
  if (antes?.coluna === coluna) sentido = antes.sentido === 'asc' ? 'desc' : 'asc';
  else {
    const valores = ls.moveis.map((tr) => valorDaCelula(textoDe(tr.cells[coluna] as Element)));
    sentido = colunaEhNumerica(valores) ? 'desc' : 'asc';
  }
  estados.set(tabela, { coluna, sentido });
  aplicar(tabela);
  marcarCabecalho(tabela);
}

/** Liga a ordenação no app inteiro. Devolve a função que desliga (para teste). */
export function ordenarTabelasDoApp(raiz: HTMLElement = document.body): () => void {
  const pendentes = new Set<HTMLTableElement>();
  let agendado = false;
  const passar = () => {
    agendado = false;
    const tabelas = pendentes.size ? [...pendentes] : Array.from(raiz.querySelectorAll('table'));
    pendentes.clear();
    for (const t of tabelas) {
      if (!t.isConnected) continue;
      aplicar(t);
      marcarCabecalho(t);
    }
  };
  const agendar = () => {
    if (agendado) return;
    agendado = true;
    requestAnimationFrame(passar);
  };
  const observador = new MutationObserver((registros) => {
    for (const r of registros) {
      const alvo = r.target instanceof Element ? r.target : r.target.parentElement;
      const t = alvo?.closest('table');
      if (t instanceof HTMLTableElement) pendentes.add(t);
      for (const no of Array.from(r.addedNodes))
        if (no instanceof Element)
          for (const x of [
            ...(no instanceof HTMLTableElement ? [no] : []),
            ...Array.from(no.querySelectorAll('table')),
          ])
            pendentes.add(x);
    }
    if (pendentes.size) agendar();
  });
  // texto também: a tela que só troca o valor de uma célula precisa reordenar
  observador.observe(raiz, { childList: true, subtree: true, characterData: true });
  const clique = (ev: MouseEvent) => {
    const th = (ev.target as Element | null)?.closest?.('th[data-ordenavel]');
    if (th instanceof HTMLTableCellElement) aoEscolher(th);
  };
  const tecla = (ev: KeyboardEvent) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const th = (ev.target as Element | null)?.closest?.('th[data-ordenavel]');
    if (th instanceof HTMLTableCellElement) {
      ev.preventDefault();
      aoEscolher(th);
    }
  };
  raiz.addEventListener('click', clique);
  raiz.addEventListener('keydown', tecla);
  agendar();
  return () => {
    observador.disconnect();
    raiz.removeEventListener('click', clique);
    raiz.removeEventListener('keydown', tecla);
  };
}
